import axios, {
  AxiosError,
  AxiosPromise,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { v4 } from "uuid";

export type IAxiosMatcherObject = {
  urlSubstring?: string;
  method?: string; // TODO support set of methods? Or maybe read/write?
  success?: boolean;
  cb?: (param: AxiosResponse) => boolean;
};
export type TAxiosMatcherFunc = (param: AxiosResponse) => boolean;
export type TAxiosMatcher = TAxiosMatcherFunc | IAxiosMatcherObject | string;
export type TAxiosResponseEmitter = (param: AxiosResponse) => void;
export type IAxiosTracker = {
  matcher: TAxiosMatcher;
  responseEmitter: TAxiosResponseEmitter;
};

export class AxiosTracker {
  public trackers: { [id: string]: IAxiosTracker };

  constructor() {
    this.trackers = {};
    this.requestInterceptor = this.requestInterceptor.bind(this);
    this.responseInterceptor = this.responseInterceptor.bind(this);
    axios.interceptors.request.use(
      this.requestInterceptor,
      this.failureInterceptor,
    );
    axios.interceptors.response.use(
      this.responseInterceptor,
      this.failureInterceptor,
    );
  }

  public isMatch(matcher: TAxiosMatcher, response: AxiosResponse): boolean {
    // normalize to matcher object with lowercase strings
    if (typeof matcher === "function") {
      matcher = { cb: matcher };
    }
    if (typeof matcher === "string") {
      matcher = { urlSubstring: matcher };
    }
    matcher = { ...matcher };
    matcher.method = matcher.method?.toLowerCase();
    matcher.urlSubstring = matcher.urlSubstring?.toLowerCase();

    // evaluate match conditions
    const url: string = response.config.url || "";
    const urlMatch: boolean =
      matcher.urlSubstring === undefined ||
      url.indexOf(matcher.urlSubstring) !== -1;
    const method = response.config.method?.toLowerCase();
    const methodMatch: boolean =
      matcher.method === undefined || matcher.method === method;
    const successMatch: boolean =
      matcher.success === undefined ||
      (matcher.success && response.status >= 200 && response.status < 400) ||
      (!matcher.success && response.status >= 400);
    const cbMatch: boolean = matcher.cb === undefined || matcher.cb(response);

    return urlMatch && successMatch && cbMatch && methodMatch;
  }

  // TODO - remove "if" when orion supports header
  public requestInterceptor(config: InternalAxiosRequestConfig) {
    if (config.url && config.url.indexOf("/api/remote-sensing/") === -1) {
      config.headers.set("x-granular-transaction-id", v4());
    }
    return config;
  }

  public responseInterceptor(response: AxiosResponse): AxiosResponse {
    for (const key in this.trackers) {
      if (this.trackers.hasOwnProperty(key)) {
        const tracker: IAxiosTracker = this.trackers[key];
        if (this.isMatch(tracker.matcher, response)) {
          if (tracker.responseEmitter) {
            try {
              tracker.responseEmitter(response);
            } catch (e) {
              // tslint:disable-next-line:no-console
              console.warn(`bad axios response emitter: ${key}`);
            }
          }
        }
      }
    }
    return response;
  }

  public failureInterceptor(error: AxiosError): AxiosPromise {
    return Promise.reject(error);
  }

  // returns key which can be used to remove tracker
  public registerTracker(
    matcher: TAxiosMatcher,
    responseEmitter: TAxiosResponseEmitter = (_param: AxiosResponse) => true,
  ): string {
    const key = v4();
    this.trackers[key] = { matcher, responseEmitter };
    return key;
  }

  // convenience function for registering array of trackers
  public registerTrackerArray(trackerArray: IAxiosTracker[]): string[] {
    const keys: string[] = [];
    trackerArray.forEach((aTracker: IAxiosTracker) => {
      keys.push(
        this.registerTracker(aTracker.matcher, aTracker.responseEmitter),
      );
    });
    return keys;
  }

  public removeTracker(key: string) {
    delete this.trackers[key];
  }
}

export const axiosTracker = new AxiosTracker();
